import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import { safeJson } from '../utils/safeJson';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { 
  ExternalLink, 
  X, 
  Flag,
  Megaphone
} from 'lucide-react';

/**
 * SponsoredAd Component
 * Displays sponsored ads in the feed. Fetches active campaigns and shows them
 * with proper "Sponsored" labeling and CTA buttons.
 */
export default function SponsoredAd({ placement = 'feed', className = '' }) {
  const { user, getAuthHeaders } = useAuth();
  const [ad, setAd] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [reported, setReported] = useState(false);

  const fetchAd = useCallback(async () => {
    try {
      const res = await fetch(`${API}/ads/feed?limit=1`, {
        headers: user ? getAuthHeaders() : {}
      });
      
      if (res.ok) {
        const result = await safeJson(res);
        if (result.ok && result.data && result.data.ads && result.data.ads.length > 0) {
          setAd(result.data.ads[0]);
          trackImpression(result.data.ads[0].campaign_id);
        }
      }
    } catch (error) {
      console.error('Failed to fetch ad:', error);
    }
  }, [user, getAuthHeaders]);

  useEffect(() => {
    fetchAd();
  }, [fetchAd]);

  const trackImpression = async (campaignId) => {
    try {
      await fetch(`${API}/ads/${campaignId}/impression`, {
        method: 'POST',
        headers: user ? getAuthHeaders() : {}
      });
    } catch (error) {
      // Silently fail
    }
  };

  const trackClick = async (campaignId) => {
    try {
      await fetch(`${API}/ads/${campaignId}/click`, {
        method: 'POST',
        headers: user ? getAuthHeaders() : {}
      });
    } catch (error) {
      // Silently fail
    }
  };

  const handleHide = async () => {
    if (ad && user) {
      try {
        await fetch(`${API}/ads/${ad.campaign_id}/hide`, {
          method: 'POST',
          headers: getAuthHeaders()
        });
      } catch (error) {
        // Silently fail
      }
    }
    setHidden(true);
  };

  const handleReport = async () => {
    if (ad && user) {
      try {
        await fetch(`${API}/ads/${ad.campaign_id}/report`, {
          method: 'POST',
          headers: getAuthHeaders()
        });
        setReported(true);
      } catch (error) {
        // Silently fail
      }
    }
  };

  const handleCtaClick = () => {
    if (ad) {
      trackClick(ad.campaign_id);
      if (ad.content?.cta_url) {
        window.open(ad.content.cta_url, '_blank');
      }
    }
  };

  // Don't render if hidden, reported, or no ad
  if (hidden || reported || !ad) return null;

  return (
    <Card className={`overflow-hidden border border-border/50 ${className}`} data-testid="sponsored-ad">
      {/* Sponsored Header */}
      <div className="flex items-center justify-between px-4 py-2 bg-muted/30 border-b border-border/50">
        <div className="flex items-center gap-2">
          <Megaphone className="w-4 h-4 text-primary" />
          <Badge variant="outline" className="text-xs">Sponsored</Badge>
        </div>
        <div className="flex items-center gap-1">
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
            onClick={handleReport}
            title="Report ad"
          >
            <Flag className="w-3 h-3" />
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
            onClick={handleHide}
            title="Hide ad"
          >
            <X className="w-3 h-3" />
          </Button>
        </div>
      </div>

      {/* Ad Content */}
      <div className="p-4">
        {/* Image if banner or sponsored post */}
        {ad.content?.image_url && (
          <div className="mb-3 -mx-4 -mt-4 overflow-hidden">
            <img 
              src={ad.content.image_url} 
              alt={ad.content.headline || 'Sponsored content'}
              className="w-full h-48 object-cover"
            />
          </div>
        )}

        {/* Video if video ad */}
        {ad.ad_type === 'video' && ad.content?.video_url && (
          <div className="mb-3 -mx-4 -mt-4 overflow-hidden">
            <video 
              src={ad.content.video_url}
              poster={ad.content.thumbnail_url}
              className="w-full h-48 object-cover"
              controls
              muted
            />
          </div>
        )}

        {/* Headline */}
        {ad.content?.headline && (
          <h3 className="font-semibold text-lg mb-2">{ad.content.headline}</h3>
        )}

        {/* Body text */}
        {ad.content?.body_text && (
          <p className="text-muted-foreground text-sm mb-3">{ad.content.body_text}</p>
        )}

        {/* CTA Button */}
        {ad.content?.cta_text && (
          <Button 
            onClick={handleCtaClick}
            className="w-full"
            data-testid="ad-cta-button"
          >
            {ad.content.cta_text}
            <ExternalLink className="w-4 h-4 ml-2" />
          </Button>
        )}

        {/* Advertiser info */}
        <p className="text-xs text-muted-foreground mt-3 text-center">
          Ad by {ad.advertiser_name || 'Advertiser'}
        </p>
      </div>
    </Card>
  );
}
